import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * Modal de pagamento PIX para compra de posts individuais e assinaturas
 */
const PixPostModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentType, setPaymentType] = useState('post'); // 'post' ou 'subscription'

  useEffect(() => {
    // Listener para compra de post
    const handleOpenPixModal = (event) => {
      const { qr_code, qr_code_base64, payment_id, amount, type } = event.detail;
      if (type === 'post') {
        setPixData({ qr_code, qr_code_base64, payment_id, amount });
        setPaymentType('post');
        setIsOpen(true);
      }
    };

    // Listener para assinatura
    const handleOpenPixSubscriptionModal = (event) => {
      const { qr_code, qr_code_base64, payment_id, amount, plan, creator_name } = event.detail;
      setPixData({ qr_code, qr_code_base64, payment_id, amount, plan, creator_name });
      setPaymentType('subscription');
      setIsOpen(true);
    };

    window.addEventListener('openPixModal', handleOpenPixModal);
    window.addEventListener('openPixSubscriptionModal', handleOpenPixSubscriptionModal);
    
    return () => {
      window.removeEventListener('openPixModal', handleOpenPixModal);
      window.removeEventListener('openPixSubscriptionModal', handleOpenPixSubscriptionModal);
    };
  }, []);

  // Polling para verificar status do pagamento
  useEffect(() => {
    let interval;
    if (isOpen && pixData?.payment_id) {
      interval = setInterval(async () => {
        try {
          setChecking(true);
          const token = localStorage.getItem('token');
          
          // Endpoint diferente para post vs assinatura
          const endpoint = paymentType === 'subscription' 
            ? `${API}/api/pix/status/${pixData.payment_id}`
            : `${API}/api/checkout/pix-status/${pixData.payment_id}`;
          
          const response = await axios.get(endpoint, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.status === 'approved') {
            const successMessage = paymentType === 'subscription'
              ? 'Assinatura ativada com sucesso!'
              : 'Pagamento aprovado! Conteúdo liberado.';
            toast.success(successMessage);
            setIsOpen(false);
            setPixData(null);
            // Recarregar página para mostrar conteúdo
            window.location.reload();
          }
        } catch (error) {
          console.error('Error checking PIX status:', error);
        } finally {
          setChecking(false);
        }
      }, 5000); // Verificar a cada 5 segundos
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOpen, pixData?.payment_id, paymentType]);

  const handleCopyCode = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setPixData(null);
    setCopied(false);
  };

  if (!isOpen || !pixData) return null;

  const getPlanName = (plan) => {
    switch (plan) {
      case 'monthly': return 'Mensal';
      case 'quarterly': return 'Trimestral';
      case 'annual': return 'Anual';
      default: return plan;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-[#1A1A1A] rounded-2xl max-w-md w-full border border-[#C79F5E]/30 shadow-2xl shadow-[#C79F5E]/20 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <span className="text-2xl">{paymentType === 'subscription' ? '⭐' : '🔐'}</span>
              {paymentType === 'subscription' ? 'Assinatura PIX' : 'Pagamento PIX'}
            </h3>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Info da Assinatura (se for assinatura) */}
          {paymentType === 'subscription' && pixData.creator_name && (
            <div className="bg-[#C79F5E]/10 border border-[#C79F5E]/20 rounded-lg p-3 text-center">
              <p className="text-sm text-gray-400">Assinatura de</p>
              <p className="font-semibold text-white">{pixData.creator_name}</p>
              <p className="text-xs text-[#C79F5E]">Plano {getPlanName(pixData.plan)}</p>
            </div>
          )}

          {/* Valor */}
          <div className="text-center">
            <p className="text-gray-400 text-sm">Valor</p>
            <p className="text-[#C79F5E] text-3xl font-bold">
              R$ {pixData.amount?.toFixed(2)}
            </p>
          </div>

          {/* QR Code */}
          {pixData.qr_code_base64 && (
            <div className="bg-white p-4 rounded-xl mx-auto w-fit">
              <img
                src={`data:image/png;base64,${pixData.qr_code_base64}`}
                alt="QR Code PIX"
                className="w-48 h-48"
              />
            </div>
          )}

          {/* Instruções */}
          <div className="text-center space-y-2">
            <p className="text-gray-300 text-sm">
              Escaneie o QR Code com o app do seu banco
            </p>
            <p className="text-gray-500 text-xs">
              ou copie o código PIX abaixo
            </p>
          </div>

          {/* Código PIX */}
          <div className="bg-[#0D0D0D] rounded-lg p-3 relative">
            <input
              type="text"
              readOnly
              value={pixData.qr_code || ''}
              className="w-full bg-transparent text-gray-300 text-xs pr-16 outline-none"
            />
            <button
              onClick={handleCopyCode}
              className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded text-xs font-medium transition-all ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-[#C79F5E] text-black hover:bg-[#D4AF6E]'
              }`}
            >
              {copied ? '✓ Copiado!' : 'Copiar'}
            </button>
          </div>

          {/* Status */}
          <div className="flex items-center justify-center gap-2 text-sm">
            {checking ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-[#C79F5E] border-t-transparent rounded-full"></div>
                <span className="text-[#C79F5E]">Verificando pagamento...</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-yellow-500">Aguardando pagamento</span>
              </>
            )}
          </div>

          {/* Botão Fechar */}
          <button
            onClick={handleClose}
            className="w-full bg-[#333] hover:bg-[#444] text-white py-3 rounded-xl font-medium transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PixPostModal;
